import {
  DragDropContext,
  Draggable,
  type DraggableProvided,
  type DraggableStateSnapshot,
  Droppable,
  type DroppableProvided,
} from "@hello-pangea/dnd";
import type React from "react";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useAuthContext, useGlobalState } from "senf-shared";
import { tv, type VariantProps } from "tailwind-variants";
import { Checkbox } from "../../../..";
import { cn } from "../../../../util/classNameConstructor";
import {
  getItemStyle,
  useOnDragEnd,
} from "../../../atoms/sortableList/Sortable";
import MagicButton from "../../magicButton/MagicButton";
import { AddButton } from "../components/AddRowButton";
import { ChoiceItem } from "../components/ChoiceItemWrapper";
import EditableContent from "../components/EditableContent";
import { Row } from "../components/SortableRow";
import { useRandomSorting } from "../hooks";
import type { QuestionValue } from "../results.config";
import type { InputCell, InputRow, Page } from "../survey.config";
import { isChecked, toggle, type useEditQuestion } from "../utils";
import Textarea from "./Textarea";

const SelectStyles = tv({
  slots: {
    Outer: "flex w-full flex-col",
    Wrapper: "w-full pb-6",
    Table: "w-full",
  },
});

export type SelectProps = Page<"choice" | "choiceImage"> &
  VariantProps<typeof SelectStyles> &
  ReturnType<typeof useEditQuestion> & {
    inputType?: "radio" | "checkbox";
    onChange: (props: Partial<QuestionValue>) => void;
    mode: "edit" | "display";
    page: Page<"choice" | "choiceImage">;
  };

const Select = ({ onChange, ...props }: SelectProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { Outer, Wrapper, Table } = SelectStyles();
  const { t } = useTranslation();
  const [translation] = useGlobalState().translationsState;
  const [generatingOptions, setGeneratingOptions] = useState(false);

  const id = useId();

  const inputType =
    props.settings?.multipleChoice?.enabled !== true ? "radio" : "checkbox";

  const handleDragStart = () => {
    ref.current?.scrollTo({ left: 0, behavior: "smooth" });
  };

  const sortedOptions = useRandomSorting(props.page, "columns", props.mode);

  const otherOption = useMemo<InputCell>(() => {
    return {
      uid: "other",
      label:
        (translation?.[props.settings.otherOption?.label || ""] ||
          props.settings.otherOption?.label) ??
        props.defaultOtherTitle,
    };
  }, [props.settings.otherOption?.label, translation]);

  const columns = useMemo(() => {
    return props.mode === "display" && props.settings.otherOption?.enabled
      ? [...sortedOptions, otherOption]
      : sortedOptions;
  }, [
    sortedOptions,
    otherOption,
    props.mode,
    props.settings.otherOption?.enabled,
  ]);

  const onDragEnd = useOnDragEnd(
    columns,
    (items: InputRow<"choice" | "choiceImage">[]) =>
      props.updateQuestion(
        (e) => ({
          ...e,
          columns: items,
        }),
        "options",
      ),
  );

  if (props.mode === "display") {
    return (
      <div className={Outer()}>
        {columns.map((column) => (
          <ChoiceItem
            key={column.uid}
            value={props.value}
            onChange={onChange}
            column={{
              ...column,
              label:
                column.uid === "other"
                  ? (props.settings.otherOption?.label ??
                    props.defaultOtherTitle)
                  : translation?.[column.label || ""] || column.label,
            }}
            question={props}
            classNames={{ label: "justify-start" }}
          />
        ))}
        {props.settings.otherOption?.enabled && (
          <div
            className={cn(
              "my-2",
              !isChecked(props.value, "other", inputType) &&
                "opacity-0 pointer-events-none",
            )}
          >
            <Textarea
              {...props}
              aria-label={otherOption.label}
              value={props.comment ?? ""}
              onChange={(comment) => onChange?.({ comment })}
              textRequired={props.settings.otherOption?.required}
              textTitle={
                props.settings.otherOption?.placeholder ??
                props.defaultTextTitle
              }
              placeholder={props.settings.otherOption?.placeholder}
              maxChar={props.settings.otherOption?.maxChars}
              rows={1}
              tabIndex={
                !isChecked(props.value, "other", inputType) ? -1 : undefined
              }
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={Outer()}>
      <div className={Wrapper()} ref={ref}>
        <div className={Table()}>
          <DragDropContext onDragEnd={onDragEnd} onDragStart={handleDragStart}>
            <Droppable droppableId="columns">
              {(droppableProvided: DroppableProvided) => (
                <div ref={droppableProvided.innerRef}>
                  {columns.map((column, i) => (
                    <Draggable
                      draggableId={column.uid}
                      index={i}
                      key={column.uid}
                      isDragDisabled={props.mode !== "edit"}
                    >
                      {(
                        provided: DraggableProvided,
                        snapshot: DraggableStateSnapshot,
                      ) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={getItemStyle(provided, snapshot, "y")}
                          tabIndex={-1}
                        >
                          <Row
                            column={column}
                            columns={columns}
                            checked={props.value || ""}
                            {...props}
                            index={i}
                            inputType={inputType}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {droppableProvided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
          {props.settings.otherOption?.enabled && (
            <Row
              column={{
                ...otherOption,
                label: props.settings.otherOption?.label,
              }}
              columns={columns}
              {...props}
              index={columns.length}
              inputType={inputType}
              defaultValue={props.defaultOtherTitle}
            />
          )}
        </div>
      </div>
      {props.settings.otherOption?.enabled &&
        (props.mode === "edit" ||
          isChecked(props.value, "other", inputType)) && (
          <div className="-mt-4 mb-5">
            <EditableContent
              mode="edit"
              defaultValue={props.defaultTextTitle}
              value={props.settings.otherOption?.placeholder}
              onChange={(event) => {
                props.updateOtherOption({
                  placeholder: event.target.value ?? null,
                });
              }}
              className="body-md text-text-subdued"
              silent={false}
            />
          </div>
        )}
      <div className="flex gap-3 -mx-0.5">
        <AddButton
          text={t("global:option")}
          onClick={() => props.addItem("columns", { focus: true })}
          classNameWrapper="mr-auto"
          id={`pageOptions-addButton-${id}`}
          show={props.mode === "edit"}
          disabled={generatingOptions}
        />

        <MagicButton
          text={t("util:generate", { val: t("global:options") })}
          leadingIcon={generatingOptions ? "loading" : "ai"}
          disabled={generatingOptions}
          onClick={() => {
            setGeneratingOptions(true);
            props
              .generateItems("columns", props.page, {
                focus: true,
                generateType: "more",
              })
              .finally(() => {
                setGeneratingOptions(false);
              });
          }}
          classNameWrapper="mr-auto whitespace-nowrap"
          id={`pageOptions-magicButton-${id}`}
          show={props.mode === "edit"}
        />
      </div>
    </div>
  );
};
Select.displayName = "Select";
export default Select;
